import { Button } from '@atlas/ui-primitives'

export interface PaginationFooterProps {
	/** 1-based current page. */
	page: number
	/** Total pages, as reported by the endpoint — at least 1. */
	totalPages: number
	/** Unpaginated total across every page. */
	total: number
	/** Singular noun for the counted thing, e.g. `delivery`, `event`. */
	noun: string
	/** Plural of `noun`. Explicit rather than derived — English plurals are not a `+ 's'` rule. */
	nounPlural: string
	/** Disables both steppers while a page is in flight, so a fast clicker cannot outrun the fetch. */
	isFetching?: boolean
	onPage: (next: number) => void
}

/**
 * Page count and stepper.
 *
 * Shows a REAL total because the endpoints behind it return one — "page 2 of
 * 14" rather than the "was the page full?" guess a caller has to make against a
 * list that reports no count. Renders nothing at all when there is nothing to
 * page: an empty result's own empty state says more than "0 events · page 1 of 1".
 */
export function PaginationFooter({
	page,
	totalPages,
	total,
	noun,
	nounPlural,
	isFetching = false,
	onPage,
}: PaginationFooterProps) {
	if (total === 0) return null
	return (
		<div className="mt-4 flex items-center justify-between text-ds-sm text-ds-text-muted">
			<span>
				{total} {total === 1 ? noun : nounPlural} · page {page} of {totalPages}
			</span>
			<div className="flex gap-2">
				<Button variant="outline" size="sm" isDisabled={page <= 1 || isFetching} onPress={() => onPage(page - 1)}>
					‹ Prev
				</Button>
				<Button
					variant="outline"
					size="sm"
					isDisabled={page >= totalPages || isFetching}
					onPress={() => onPage(page + 1)}
				>
					Next ›
				</Button>
			</div>
		</div>
	)
}
